import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Brain,
  MessageSquare,
  CheckCircle,
  XCircle,
  HelpCircle,
  AlertTriangle,
  History,
  BarChart3,
  Sparkles,
  RefreshCw,
  Send,
  Eye,
  EyeOff,
  GitCompare,
  Clock,
  ChevronDown,
  Info,
  Loader2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Skeleton } from '../../../components/ui/skeleton';

/**
 * HT-Core-03: Representation Workspace
 * 
 * Purpose: Test if HumaTwin can represent the user accurately.
 * This is separate from Identity Formation which shows what Twin knows.
 * 
 * Flow:
 * 1. Create/select scenario
 * 2. Twin generates representation (or abstains)
 * 3. User evaluates representation
 * 4. Track fidelity over time
 */

export default function RepresentationWorkspace() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  
  // States
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  
  // Data states
  const [fidelitySummary, setFidelitySummary] = useState(null);
  const [contextFidelity, setContextFidelity] = useState([]);
  const [history, setHistory] = useState([]);
  
  // Current scenario/representation states
  const [scenarioText, setScenarioText] = useState('');
  const [scenarioMode, setScenarioMode] = useState('open'); // open or blind_comparison
  const [currentScenario, setCurrentScenario] = useState(null);
  const [currentRepresentation, setCurrentRepresentation] = useState(null);
  
  // Blind comparison states
  const [userAnswer, setUserAnswer] = useState('');
  const [showTwinPrediction, setShowTwinPrediction] = useState(false);
  
  // Selected representation for detail view
  const [selectedRepresentation, setSelectedRepresentation] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('new');
  
  // Set RTL direction
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);
  
  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [fidelityRes, contextRes, historyRes] = await Promise.all([
        apiClient.get('/representation/fidelity'),
        apiClient.get('/representation/fidelity/by-context'),
        apiClient.get('/representation/history?limit=20')
      ]);
      
      setFidelitySummary(fidelityRes.data);
      setContextFidelity(contextRes.data.contexts || []);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Create scenario and generate representation
  const handleCreateScenario = async () => {
    if (!scenarioText.trim()) {
      toast.error(t('representation.enterScenario', 'Please enter a scenario'));
      return;
    }
    
    setGenerating(true);
    try {
      // Create scenario
      const scenarioRes = await apiClient.post('/representation/scenarios', {
        scenario_text: scenarioText,
        mode: scenarioMode
      });
      
      const scenario = scenarioRes.data;
      setCurrentScenario(scenario);
      
      // Generate representation
      const repRes = await apiClient.post('/representation/generate', {
        scenario_id: scenario.scenario_id
      });
      
      setCurrentRepresentation(repRes.data);
      
      // For blind comparison, don't show prediction yet
      if (scenarioMode === 'blind_comparison' && repRes.data.status === 'awaiting_user_answer') {
        setShowTwinPrediction(false);
        toast.success(t('representation.answerFirst', 'Now answer the scenario yourself before seeing the Twin\'s prediction'));
      } else if (repRes.data.status === 'abstained') {
        toast.info(t('representation.abstained', 'Your Twin does not have enough evidence for this scenario yet'));
      } else {
        toast.success(t('representation.generated', 'Representation generated'));
      }
      
    } catch (error) {
      console.error('Failed to create scenario:', error);
      toast.error(t('representation.error', 'Failed to generate representation'));
    } finally {
      setGenerating(false);
    }
  };
  
  // Submit user answer for blind comparison
  const handleSubmitUserAnswer = async () => {
    if (!userAnswer.trim()) {
      toast.error(t('representation.enterAnswer', 'Please enter your answer'));
      return;
    }
    
    setGenerating(true);
    try {
      const res = await apiClient.post('/representation/user-answer', {
        representation_id: currentRepresentation.representation_id,
        user_answer: userAnswer
      });
      
      setCurrentRepresentation(res.data);
      setShowTwinPrediction(true);
      toast.success(t('representation.comparisonReady', 'Now compare your answer with the Twin\'s prediction'));
      
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error(t('representation.error', 'Failed to submit answer'));
    } finally {
      setGenerating(false);
    }
  };
  
  // Evaluate representation
  const handleEvaluate = async (rating, correction = null) => {
    setEvaluating(true);
    try {
      await apiClient.post('/representation/evaluate', {
        representation_id: currentRepresentation.representation_id,
        rating: rating,
        correction_text: correction
      });
      
      toast.success(t('representation.evaluated', 'Thank you for your feedback!'));
      
      // Reset and refresh
      setCurrentScenario(null);
      setCurrentRepresentation(null);
      setScenarioText('');
      setUserAnswer('');
      setShowTwinPrediction(false);
      fetchData();
      
    } catch (error) {
      console.error('Failed to evaluate:', error);
      toast.error(t('representation.evaluateError', 'Failed to submit evaluation'));
    } finally {
      setEvaluating(false);
    }
  };
  
  // View representation detail
  const handleViewDetail = async (representationId) => {
    try {
      const res = await apiClient.get(`/representation/${representationId}`);
      setSelectedRepresentation(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Failed to fetch detail:', error);
    }
  };
  
  // Rating badge colors
  const getRatingColor = (rating) => {
    const colors = {
      'represents_me': 'bg-green-100 text-green-700',
      'mostly_represents_me': 'bg-blue-100 text-blue-700',
      'partly_represents_me': 'bg-amber-100 text-amber-700',
      'does_not_represent_me': 'bg-red-100 text-red-700',
      'unsure': 'bg-gray-100 text-gray-600'
    };
    return colors[rating] || colors['unsure'];
  };
  
  const getRatingLabel = (rating) => {
    const labels = {
      'represents_me': t('representation.ratings.represents', 'Represents me'),
      'mostly_represents_me': t('representation.ratings.mostly', 'Mostly represents me'),
      'partly_represents_me': t('representation.ratings.partly', 'Partly represents me'),
      'does_not_represent_me': t('representation.ratings.doesNot', 'Does not represent me'),
      'unsure': t('representation.ratings.unsure', 'Unsure')
    };
    return labels[rating] || rating;
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button variant="ghost" size="icon" onClick={() => navigate('/personal')}>
              <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Brain className="w-5 h-5 text-indigo-600" />
                {t('representation.title', 'Representation')}
              </h1>
              <p className="text-sm text-gray-500">
                {t('representation.subtitle', 'Can your Twin represent you accurately?')}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Fidelity Overview Card */}
        <Card className="mb-6" data-testid="fidelity-overview">
          <CardHeader className="pb-2">
            <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              {t('representation.fidelityOverview', 'Representation Fidelity')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex gap-4">
                <Skeleton className="h-20 w-24" />
                <Skeleton className="h-20 w-24" />
                <Skeleton className="h-20 w-24" />
              </div>
            ) : fidelitySummary ? (
              <div className="space-y-4">
                <div className={`flex flex-wrap gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="text-center p-3 bg-gray-50 rounded-lg min-w-[100px]">
                    <div className="text-2xl font-bold text-gray-900">{fidelitySummary.total_evaluated}</div>
                    <div className="text-xs text-gray-500">{t('representation.evaluated', 'Evaluated')}</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg min-w-[100px]">
                    <div className="text-2xl font-bold text-green-700">{fidelitySummary.strong_match_count}</div>
                    <div className="text-xs text-green-600">{t('representation.strongMatches', 'Strong Matches')}</div>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg min-w-[100px]">
                    <div className="text-2xl font-bold text-amber-700">{fidelitySummary.partly_count + fidelitySummary.does_not_count}</div>
                    <div className="text-xs text-amber-600">{t('representation.mismatches', 'Mismatches')}</div>
                  </div>
                </div>
                <p className={`text-sm text-gray-600 ${isRTL ? 'text-right' : ''}`}>
                  {fidelitySummary.summary_text}
                </p>
              </div>
            ) : (
              <p className="text-gray-500">{t('representation.noData', 'No evaluations yet')}</p>
            )}
          </CardContent>
        </Card>
        
        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="new" data-testid="tab-new">
              <Sparkles className="w-4 h-4 mr-2" />
              {t('representation.newScenario', 'New Scenario')}
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="w-4 h-4 mr-2" />
              {t('representation.history', 'History')}
            </TabsTrigger>
            <TabsTrigger value="contexts" data-testid="tab-contexts">
              <BarChart3 className="w-4 h-4 mr-2" />
              {t('representation.byContext', 'By Context')}
            </TabsTrigger>
          </TabsList>
          
          {/* New Scenario Tab */}
          <TabsContent value="new" className="space-y-4">
            {!currentRepresentation ? (
              <Card data-testid="scenario-input">
                <CardHeader>
                  <CardTitle className={`text-lg ${isRTL ? 'text-right' : ''}`}>
                    {t('representation.createScenario', 'Create a Scenario')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className={`text-sm text-gray-600 ${isRTL ? 'text-right' : ''}`}>
                    {t('representation.scenarioHelp', 'Describe a situation to test how well your Twin can represent your likely perspective.')}
                  </p>
                  
                  <Textarea
                    value={scenarioText}
                    onChange={(e) => setScenarioText(e.target.value)}
                    placeholder={t('representation.scenarioPlaceholder', 'Example: You receive a job offer that pays more but requires relocating away from family...')}
                    className="min-h-[120px]"
                    data-testid="scenario-input-field"
                  />
                  
                  {/* Mode selection */}
                  <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <label className="text-sm text-gray-600">{t('representation.mode', 'Mode')}:</label>
                    <Button
                      variant={scenarioMode === 'open' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setScenarioMode('open')}
                      data-testid="mode-open"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      {t('representation.modeOpen', 'Open')}
                    </Button>
                    <Button
                      variant={scenarioMode === 'blind_comparison' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setScenarioMode('blind_comparison')}
                      data-testid="mode-blind"
                    >
                      <GitCompare className="w-4 h-4 mr-1" />
                      {t('representation.modeBlind', 'Blind Comparison')}
                    </Button>
                  </div>
                  
                  <p className="text-xs text-gray-500">
                    {scenarioMode === 'open' 
                      ? t('representation.openHelp', 'See the Twin\'s representation first, then evaluate')
                      : t('representation.blindHelp', 'Answer yourself first, then compare with Twin\'s prediction')}
                  </p>
                  
                  <Button 
                    onClick={handleCreateScenario}
                    disabled={generating || !scenarioText.trim()}
                    className="w-full"
                    data-testid="generate-btn"
                  >
                    {generating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('representation.generating', 'Generating...')}</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" />{t('representation.generate', 'Generate Representation')}</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Scenario Display */}
                <Card data-testid="scenario-display">
                  <CardHeader>
                    <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <MessageSquare className="w-5 h-5 text-indigo-600" />
                      {t('representation.theScenario', 'The Scenario')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-gray-700 ${isRTL ? 'text-right' : ''}`}>{currentScenario?.scenario_text}</p>
                    {currentScenario?.contexts?.length > 0 && (
                      <div className={`flex gap-1 mt-2 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
                        {currentScenario.contexts.map((ctx, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{ctx}</Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Abstention Display */}
                {currentRepresentation.status === 'abstained' && (
                  <Card className="border-amber-200 bg-amber-50" data-testid="abstention-display">
                    <CardContent className="pt-6">
                      <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                        <div>
                          <h3 className={`font-medium text-amber-800 ${isRTL ? 'text-right' : ''}`}>
                            {t('representation.insufficientEvidence', 'Insufficient Evidence')}
                          </h3>
                          <p className={`text-sm text-amber-700 mt-1 ${isRTL ? 'text-right' : ''}`}>
                            {currentRepresentation.abstention_reason || currentRepresentation.message}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="mt-4 w-full"
                        onClick={() => {
                          setCurrentScenario(null);
                          setCurrentRepresentation(null);
                          setScenarioText('');
                        }}
                      >
                        {t('representation.tryAnother', 'Try Another Scenario')}
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {/* Blind Comparison: User Answer First */}
                {currentRepresentation.status === 'awaiting_user_answer' && !showTwinPrediction && (
                  <Card data-testid="user-answer-input">
                    <CardHeader>
                      <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <EyeOff className="w-5 h-5 text-purple-600" />
                        {t('representation.yourAnswer', 'Your Answer First')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className={`text-sm text-gray-600 ${isRTL ? 'text-right' : ''}`}>
                        {t('representation.blindInstruction', 'Answer this scenario yourself before seeing how your Twin would respond.')}
                      </p>
                      <Textarea
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        placeholder={t('representation.yourAnswerPlaceholder', 'How would you approach this situation?')}
                        className="min-h-[120px]"
                        data-testid="user-answer-field"
                      />
                      <Button 
                        onClick={handleSubmitUserAnswer}
                        disabled={generating || !userAnswer.trim()}
                        className="w-full"
                        data-testid="submit-answer-btn"
                      >
                        {generating ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('representation.submitting', 'Submitting...')}</>
                        ) : (
                          <><Send className="w-4 h-4 mr-2" />{t('representation.submitAndCompare', 'Submit & Compare')}</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {/* Twin Representation Display */}
                {(currentRepresentation.status === 'awaiting_evaluation' || 
                  (currentRepresentation.status === 'generated' && scenarioMode === 'open')) && (
                  <Card data-testid="twin-representation">
                    <CardHeader>
                      <CardTitle className={`text-lg flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Brain className="w-5 h-5 text-indigo-600" />
                        {t('representation.twinRepresentation', 'Your Twin\'s Representation')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className={`p-4 bg-indigo-50 rounded-lg ${isRTL ? 'text-right' : ''}`}>
                        <p className="text-gray-800">
                          {currentRepresentation.representation_text || currentRepresentation.twin_prediction}
                        </p>
                      </div>
                      
                      {/* User's answer in blind comparison */}
                      {currentRepresentation.user_answer && (
                        <div className={`p-4 bg-purple-50 rounded-lg ${isRTL ? 'text-right' : ''}`}>
                          <p className={`text-sm font-medium text-purple-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                            {t('representation.yourActualAnswer', 'Your Actual Answer:')}
                          </p>
                          <p className="text-gray-800">{currentRepresentation.user_answer}</p>
                        </div>
                      )}
                      
                      {/* Semantic Comparison */}
                      {currentRepresentation.semantic_comparison && (
                        <div className={`p-3 bg-gray-50 rounded-lg ${isRTL ? 'text-right' : ''}`}>
                          <p className={`text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <GitCompare className="w-4 h-4" />
                            {t('representation.comparison', 'Comparison')}
                          </p>
                          <Badge className={
                            currentRepresentation.semantic_comparison.overall_alignment === 'high' ? 'bg-green-100 text-green-700' :
                            currentRepresentation.semantic_comparison.overall_alignment === 'moderate' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {currentRepresentation.semantic_comparison.overall_alignment} alignment
                          </Badge>
                        </div>
                      )}
                      
                      {/* Why Twin Said This (Structured Basis) */}
                      {currentRepresentation.key_identity_factors?.length > 0 && (
                        <div className={`p-3 bg-gray-50 rounded-lg ${isRTL ? 'text-right' : ''}`} data-testid="structured-basis">
                          <p className={`text-sm font-medium text-gray-700 mb-2 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Info className="w-4 h-4" />
                            {t('representation.whyTwinSaidThis', 'Why your Twin represented you this way')}
                          </p>
                          <ul className={`space-y-1 text-sm text-gray-600 ${isRTL ? 'pr-4' : 'pl-4'}`}>
                            {currentRepresentation.key_identity_factors.map((factor, i) => (
                              <li key={i} className="list-disc">{factor}</li>
                            ))}
                          </ul>
                          {currentRepresentation.tensions_acknowledged?.length > 0 && (
                            <p className="text-sm text-amber-600 mt-2">
                              {t('representation.tensionsConsidered', 'Value tensions considered:')} {currentRepresentation.tensions_acknowledged.join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* Evaluation Controls */}
                {currentRepresentation.status === 'awaiting_evaluation' && (
                  <EvaluationCard 
                    onEvaluate={handleEvaluate}
                    evaluating={evaluating}
                    isRTL={isRTL}
                    t={t}
                  />
                )}
              </div>
            )}
          </TabsContent>
          
          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className={`text-lg ${isRTL ? 'text-right' : ''}`}>
                  {t('representation.historyTitle', 'Representation History')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16" />
                    <Skeleton className="h-16" />
                    <Skeleton className="h-16" />
                  </div>
                ) : history.length === 0 ? (
                  <p className={`text-gray-500 text-center py-8 ${isRTL ? 'text-right' : ''}`}>
                    {t('representation.noHistory', 'No representations yet. Create a scenario to test your Twin.')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {history.map((item) => (
                      <div 
                        key={item.representation_id}
                        className={`p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors ${isRTL ? 'text-right' : ''}`}
                        onClick={() => handleViewDetail(item.representation_id)}
                        data-testid={`history-item-${item.representation_id}`}
                      >
                        <div className={`flex items-start justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 truncate">{item.scenario_preview}</p>
                            <div className={`flex items-center gap-2 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              {item.contexts?.map((ctx, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{ctx}</Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            {item.was_abstention ? (
                              <Badge className="bg-gray-100 text-gray-600">
                                {t('representation.abstained', 'Abstained')}
                              </Badge>
                            ) : item.evaluation ? (
                              <Badge className={getRatingColor(item.evaluation)}>
                                {getRatingLabel(item.evaluation)}
                              </Badge>
                            ) : (
                              <Badge variant="outline">
                                {t('representation.pendingEval', 'Pending')}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          {item.generated_at && new Date(item.generated_at).toLocaleDateString(i18n.language)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Context Fidelity Tab */}
          <TabsContent value="contexts">
            <Card>
              <CardHeader>
                <CardTitle className={`text-lg ${isRTL ? 'text-right' : ''}`}>
                  {t('representation.fidelityByContext', 'Fidelity by Context')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16" />
                    <Skeleton className="h-16" />
                  </div>
                ) : contextFidelity.length === 0 ? (
                  <p className={`text-gray-500 text-center py-8 ${isRTL ? 'text-right' : ''}`}>
                    {t('representation.noContextData', 'Evaluate more representations to see fidelity by context.')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {contextFidelity.map((ctx) => (
                      <div 
                        key={ctx.context}
                        className={`p-3 bg-gray-50 rounded-lg ${isRTL ? 'text-right' : ''}`}
                        data-testid={`context-fidelity-${ctx.context}`}
                      >
                        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="font-medium text-gray-700 capitalize">{ctx.context}</span>
                          <span className="text-sm text-gray-500">
                            {ctx.total_evaluated} {t('representation.evaluated', 'evaluated')}
                          </span>
                        </div>
                        <p className={`text-sm mt-1 ${ctx.has_enough_data ? 'text-green-600' : 'text-gray-500'}`}>
                          {ctx.display_message}
                        </p>
                        {ctx.has_enough_data && (
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full" 
                              style={{ width: `${(ctx.strong_match_count / ctx.scored_total) * 100}%` }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Brain className="w-5 h-5 text-indigo-600" />
              {t('representation.representationDetail', 'Representation Detail')}
            </DialogTitle>
          </DialogHeader>
          {selectedRepresentation && (
            <div className="space-y-4">
              {/* Scenario */}
              <div>
                <h4 className={`text-sm font-medium text-gray-500 mb-1 ${isRTL ? 'text-right' : ''}`}>
                  {t('representation.scenario', 'Scenario')}
                </h4>
                <p className={`text-gray-700 ${isRTL ? 'text-right' : ''}`}>
                  {selectedRepresentation.scenario_text}
                </p>
              </div>
              
              {/* Representation */}
              {selectedRepresentation.representation_text && (
                <div>
                  <h4 className={`text-sm font-medium text-gray-500 mb-1 ${isRTL ? 'text-right' : ''}`}>
                    {t('representation.twinSaid', 'Your Twin Said')}
                  </h4>
                  <div className={`p-3 bg-indigo-50 rounded-lg ${isRTL ? 'text-right' : ''}`}>
                    <p className="text-gray-700">{selectedRepresentation.representation_text}</p>
                  </div>
                </div>
              )}
              
              {/* Abstention */}
              {selectedRepresentation.abstention_reason && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className={`text-amber-700 ${isRTL ? 'text-right' : ''}`}>
                    {selectedRepresentation.abstention_reason}
                  </p>
                </div>
              )}
              
              {/* Structured Basis */}
              {selectedRepresentation.structured_basis?.length > 0 && (
                <div>
                  <h4 className={`text-sm font-medium text-gray-500 mb-2 ${isRTL ? 'text-right' : ''}`}>
                    {t('representation.whyTwinSaidThis', 'Why your Twin represented you this way')}
                  </h4>
                  <div className="space-y-2">
                    {selectedRepresentation.structured_basis.map((basis, i) => (
                      <div key={i} className={`p-2 bg-gray-50 rounded ${isRTL ? 'text-right' : ''}`}>
                        <Badge variant="outline" className="text-xs mb-1">{basis.type}</Badge>
                        <p className="text-sm text-gray-700">{basis.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Evaluation */}
              {selectedRepresentation.evaluation && (
                <div>
                  <h4 className={`text-sm font-medium text-gray-500 mb-1 ${isRTL ? 'text-right' : ''}`}>
                    {t('representation.yourEvaluation', 'Your Evaluation')}
                  </h4>
                  <Badge className={getRatingColor(selectedRepresentation.evaluation.rating)}>
                    {getRatingLabel(selectedRepresentation.evaluation.rating)}
                  </Badge>
                  {selectedRepresentation.evaluation.correction_text && (
                    <p className={`text-sm text-gray-600 mt-2 ${isRTL ? 'text-right' : ''}`}>
                      <strong>{t('representation.yourCorrection', 'Your correction')}:</strong> {selectedRepresentation.evaluation.correction_text}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Evaluation Card Component
function EvaluationCard({ onEvaluate, evaluating, isRTL, t }) {
  const [showCorrection, setShowCorrection] = useState(false);
  const [correctionText, setCorrectionText] = useState('');
  const [selectedRating, setSelectedRating] = useState(null);
  
  const ratings = [
    { value: 'represents_me', label: t('representation.ratings.represents', 'Represents me'), icon: CheckCircle, color: 'text-green-600 hover:bg-green-50' },
    { value: 'mostly_represents_me', label: t('representation.ratings.mostly', 'Mostly represents me'), icon: CheckCircle, color: 'text-blue-600 hover:bg-blue-50' },
    { value: 'partly_represents_me', label: t('representation.ratings.partly', 'Partly represents me'), icon: AlertTriangle, color: 'text-amber-600 hover:bg-amber-50' },
    { value: 'does_not_represent_me', label: t('representation.ratings.doesNot', 'Does not represent me'), icon: XCircle, color: 'text-red-600 hover:bg-red-50' },
    { value: 'unsure', label: t('representation.ratings.unsure', 'Unsure'), icon: HelpCircle, color: 'text-gray-600 hover:bg-gray-50' }
  ];
  
  const handleRatingClick = (rating) => {
    setSelectedRating(rating);
    if (rating === 'does_not_represent_me' || rating === 'partly_represents_me') {
      setShowCorrection(true);
    } else {
      onEvaluate(rating);
    }
  };
  
  const handleSubmitWithCorrection = () => {
    onEvaluate(selectedRating, correctionText || null);
  };
  
  return (
    <Card data-testid="evaluation-card">
      <CardHeader>
        <CardTitle className={`text-lg ${isRTL ? 'text-right' : ''}`}>
          {t('representation.doesThisRepresentYou', 'Does this represent you?')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!showCorrection ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {ratings.map(({ value, label, icon: Icon, color }) => (
              <Button
                key={value}
                variant="outline"
                className={`flex flex-col items-center p-3 h-auto ${color}`}
                onClick={() => handleRatingClick(value)}
                disabled={evaluating}
                data-testid={`rating-${value}`}
              >
                <Icon className="w-5 h-5 mb-1" />
                <span className="text-xs text-center">{label}</span>
              </Button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <p className={`text-sm text-gray-600 ${isRTL ? 'text-right' : ''}`}>
              {t('representation.correctionPrompt', 'How would you actually respond? (Optional but helps improve your Twin)')}
            </p>
            <Textarea
              value={correctionText}
              onChange={(e) => setCorrectionText(e.target.value)}
              placeholder={t('representation.correctionPlaceholder', 'I would actually...')}
              className="min-h-[80px]"
              data-testid="correction-input"
            />
            <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                variant="outline"
                onClick={() => setShowCorrection(false)}
              >
                {t('common.back', 'Back')}
              </Button>
              <Button
                onClick={handleSubmitWithCorrection}
                disabled={evaluating}
                data-testid="submit-evaluation-btn"
              >
                {evaluating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.submitting', 'Submitting...')}</>
                ) : (
                  t('representation.submitEvaluation', 'Submit Evaluation')
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
